import { useState, useEffect, useRef } from "react";
import { IScheduler } from "../../../types";
import { useBrandContext } from "../../../hooks/useBrandContext";
import { useDealContext } from "../../../features/deal/context";
import useAnalytics from '../../../hooks/useAnalytics';
import { Link, useParams } from "react-router-dom";
import { getAdvisorMeetings, getCompanyLanding } from "../../../core/services/gutcheck-api.service";
import { getAdvisor } from "../../../core/services/gutcheck-api.service";
import { CalendlyScheduler, DateDisplay, MeetingLinks } from "../../meeting";
import ClientProvider from "../../../modules/ClientProvider";
import Loading from "../../../components/Loading";
import { Box, Stack, Typography } from "@mui/material";
import { DocumentTitle } from "../../../components/DocumentTitle";
import { DisplayBoxBase } from "../../../components/shared/StyledComponents";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { OnboardingSteps } from "../components/OnboardingSteps";

dayjs.extend(utc);
dayjs.extend(timezone);

const decodeUserData = (encoded: string): { name: string; email: string } | null => {
    try {
        const decoded = atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
        const [name, email] = decoded.split('|').map(s => decodeURIComponent(s.split(':')[1]));
        return { name, email };
    } catch {
        return null;
    }
}


export const AdvisorApproved = () => {

    const { brandContext } = useBrandContext();
    const { companyTag: paramCompanyTag, advisorId } = useParams();
    const { brand } = brandContext;
    const companyTag = paramCompanyTag || brand?.tag;
    const { dealContext, setDealContext, loadDealContext } = useDealContext();
    const { trackAdvisorPage, trackAdvisorEvent, trackAdvisorUser } = useAnalytics();

    const [project, setProject] = useState<any>(null);
    const [surveyLink, setSurveyLink] = useState<any>(null);
    const [email, setEmail] = useState<any>("");
    const [name, setName] = useState<any>("");
    const [advisor, setAdvisor] = useState<any>(null);
    const [scheduler, setScheduler] = useState<IScheduler | null>(null);
    const [meetings, setMeetings] = useState<any[]>([]);
    const [fetched, setFetched] = useState<boolean>(false);

    const inviteProcessedRef = useRef(false);
    const schedulerHttpClient = ClientProvider.provideSchedulerClient();
    const [meetingCreated, setMeetingCreated] = useState(false);

    useEffect(() => {

        initialize();
    }, [companyTag, advisorId]);

    useEffect(() => {
        if (advisor && project) {
            fetchScheduler();
            fetchMeetings();
        }
    }, [advisor, project]);

    const initialize = async () => {
        // Step 1: Process invite parameter if it exists
        if (!inviteProcessedRef.current) {
            const searchParams = new URLSearchParams(window.location.search);
            const invite = searchParams.get('invite') ?? localStorage.getItem('invite');

            if (invite) {
                localStorage.setItem('invite', invite);
                const decodedInvite = decodeUserData(invite);
                if (decodedInvite) {
                    setEmail(decodedInvite.email || "");
                    setName(decodedInvite.name || "");
                }

                searchParams.delete('invite');
                window.history.replaceState(
                    {},
                    '',
                    window.location.pathname + (searchParams.toString() ? '?' + searchParams.toString() : '')
                );
            }

            inviteProcessedRef.current = true;
        }

        // Step 2: Fetch company landing data
        if (companyTag) {
            const { data, error } = await getCompanyLanding(companyTag);
            if (data) {
                setProject(data.project);
                setSurveyLink(data.surveyLink);

                setDealContext({
                    ...dealContext,
                    project: data.project,
                    fetched: true
                });

                const advisorPublicId = advisorId ?? localStorage.getItem("eval" + data.surveyLink?.public_id);
                if (advisorPublicId) {
                    const { data: advisorData, error } = await getAdvisor(advisorPublicId);
                    if (advisorData?.advisor) {
                        setAdvisor(advisorData.advisor);
                        // Only set email/name if not already set from invite
                        if (!email) {
                            setEmail(advisorData.advisor.user?.email_address ?? advisorData.advisor.email_address);
                        }
                        if (!name) {
                            setName(advisorData.advisor.user?.profile?.name?.split(" ")[0] ?? advisorData.advisor.name);
                        }
                        if (advisorData?.advisor?.status !== "approved") {
                            window.location.href = `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://${brand?.tag}.${process.env.REACT_APP_APP_ORIGIN}/advise`;
                        }
                    } else {
                        window.location.href = `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://${brand?.tag}.${process.env.REACT_APP_APP_ORIGIN}/advise`;
                    }
                }


            }

            // Get the scheduler for the project
            const { data: schedulerData, error: schedulerError } = await schedulerHttpClient.getPublicSchedulerByType(companyTag, "onboarding");
            if (schedulerData) {
                setScheduler(schedulerData.scheduler);
            }
        }


    };


    const fetchScheduler = async () => {
        if (advisor) {
            const { data, error } = await schedulerHttpClient.getPublicSchedulerByType(project.tag, "onboarding");
            if (error) {
                console.error("Error fetching scheduler:", error);
            } else {
                setScheduler(data.scheduler);
            }
        }
    };

    const fetchMeetings = async () => {
        if (advisor) {
            const { data, error } = await getAdvisorMeetings(advisor.public_id);
            if (data) {
                setMeetings(data.meetings);
            }
        }
    }
    const handleMeetingCreated = async () => {
        setMeetingCreated(true);

        setTimeout(async () => {
            await initialize();
            fetchMeetings();
        }, 2000);
    }



    if (!project || !scheduler || !advisor) {
        return <Loading />;
    }

    const landingLink = project.organization.custom_domain_enabled ?
        `${process.env.REACT_APP_APP_PROTOCOL || 'https'}://${project.organization.custom_domain}${process.env.REACT_APP_APP_PORT ? `:${process.env.REACT_APP_APP_PORT}` : ''}/${project.tag}` :
        `${project._origin}/advise/${project.tag}`;


    return (
        <>
            <DocumentTitle pageTitle={`${project?.organization?.name} Advisor Network for ${project?.name}`} showAttribution={false} />
            <Box sx={{
                width: '100%',
                background: 'linear-gradient(rgba(0,0,30,0.6), rgba(0,0,0,0.3)), url("/images/network.png")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '100vh',
                display: 'flex',
                height: '100%',
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: -1
            }}>
            </Box>

            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: "10px",
                maxWidth: '1200px',
                width: '100%',
                margin: '0 auto',
                height: 'fit-content',
                padding: "80px 80px",
                position: 'relative',
                zIndex: 1,
            }}>


                <Box>
                    <Box sx={{ color: 'white', marginBottom: '20px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                            <h1>Welcome, {advisor.user?.profile?.name?.split(" ")[0]}!</h1>
                            <h5>We're excited to have you as an advisor for {project?.name}.</h5>
                        </Box>

                        <Box className="display-box white" sx={{ padding: "40px 40px", maxWidth: '700px', margin: '40px auto', color: 'black' }}>
                            <Typography variant="h5" fontSize={"1.7rem"} fontWeight="bold" sx={{ textAlign: 'center' }}>
                                You've earned 20 shares for your initial feedback.
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', justifyContent: 'center', gap: '10px', margin: '0 auto', maxWidth: '600px' }}>

                                <Typography variant="body1" mt={2} sx={{ textAlign: 'left' }}>
                                    You now have access to your dashboard where you have opportunities to:
                                </Typography>
                                <ul>
                                    <li>See profiles and insights from your peer advisors</li>
                                    <li>Provide additional feedback each month</li>
                                    <li>Refer colleagues who could also be good advisors or customers</li>
                                    <li>Track the shares you earn for all of your help</li>
                                </ul>
                                <Typography variant="body1" mb={4} sx={{ textAlign: 'left' }}>
                                    You'll also be able to setup a calls with {project?.name} to learn more about the company and the product if and when you're interested in a demo or trial.
                                </Typography>
                            </Box>
                            <OnboardingSteps advisor={advisor} project={project} surveyLink={surveyLink} />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );

}; 